import { inject, Injectable } from '@angular/core';
import { ConversableType, ConversationHighlight, CrosstalkService } from '@ddv/crosstalk';
import { UserEntitlements } from '@ddv/entitlements';
import { ApiServices } from '@ddv/http';
import {
    crosstalkCommentFields,
    CrosstalkFields,
    ExportFilteredData,
    LinkConfiguration,
    TrebekConversationFields,
    WidgetData,
} from '@ddv/models';
import { map, mergeMap, Observable } from 'rxjs';

import { PublicApiResponseRow } from '../models/trebek';

@Injectable()
export class CrosstalkDataService {
    private readonly crosstalkService = inject(CrosstalkService);
    private readonly xtlkManagementLocation = inject(ApiServices.xtlkManagementLocation);

    // this only gets updated if the user has the entitlement to import comments
    // i think what this is doing is adding these keys to exports (*_crosstalk_key) to make the exported file
    // format work easier to turn around and import comments
    private readonly crosstalkSchemaFields: Map<number | string, { name: string, displayName: string }[]> = new Map();

    // this method is only useful as a side effect of what happens in fetchUpdatedConversations
    getCrosstalkSchemaFields(queryId: string): { name: string, displayName: string }[] | undefined {
        return this.crosstalkSchemaFields.get(queryId);
    }

    // this is only called by the advanced datagrid visualization
    fetchUpdatedConversations(
        clientCode: string,
        conversableType: string,
        namedQueryId: string,
        userEntitlements: UserEntitlements | undefined,
        data: (WidgetData & { links: { [key: string]: LinkConfiguration } })[],
    ): Observable<WidgetData[]> {
        return this.crosstalkService.getSchema(clientCode, conversableType)
            .pipe(
                mergeMap((schema) => {
                    this.cacheImportKeysForNamedQuery(namedQueryId, schema);

                    const keyValues = data.map((row) => schema.liftValuesForKeyFields(row));
                    return this.crosstalkService.fetchUpdatedConversations(clientCode, conversableType, keyValues)
                        .pipe(
                            map((highlights) => {
                                highlights.forEach((highlight, index) => {
                                    crosstalkCommentFields.forEach(() => {
                                        this.addCrosstalkAttachmentsToData(
                                            data[index],
                                            clientCode,
                                            highlight,
                                            !!userEntitlements?.canUploadCrosstalkAttachments());
                                    });
                                });

                                return data;
                            }));
                }),
            );
    }

    // returning the keys that were added to the export data
    // this isn't the greatest as it's closely related to the ExportFilteredData interface
    // but i don't want to mess with that yet and at least we're getting everything
    // that's relevant clearly visible here on this method
    addCrosstalkImportKeysToData(
        namedQueryId: string,
        data: WidgetData[],
        exportData: Pick<ExportFilteredData, 'data'>,
        ignoreFirstRow: boolean,  // this is only true when exporting from an advanced grid with grouped data
    ): string[] {
        const crosstalkFields = this.getCrosstalkSchemaFields(namedQueryId);
        if (!crosstalkFields) {
            return [];
        }

        data.forEach((row: WidgetData | undefined, index: number) => {
            crosstalkFields?.forEach((field) => {
                let rowNumber = index;
                if (ignoreFirstRow) {
                    if (index === 0) {
                        exportData.data[index][field.name] = '';
                    }
                    // we increase index by 1, because there is one additional row in exportData.data for Control Total, which is not present in data
                    rowNumber += 1;
                }
                const valueToString = row?.[field.displayName]?.toString() ?? '';
                exportData.data[rowNumber][field.name] = valueToString ? valueToString : '';
            });
        });

        return crosstalkFields.map((field) => field.name);
    }

    addCrosstalkLinksToData(
        clientCode: string,
        data: PublicApiResponseRow | (WidgetData & { links: { [key: string]: LinkConfiguration } }),
    ): void {
        data.links = {
            ...data.links,
            [TrebekConversationFields.ClientComment]: {
                label: 'Add Comment',
                uri: `${this.xtlkManagementLocation}/#/${clientCode}/conversation/${data.conversationId}`,
                launchType: 'modal',
                size: {
                    width: 620,
                    height: 600,
                },
            },

            [TrebekConversationFields.HSComment]: {
                label: 'Add Comment',
                uri: `${this.xtlkManagementLocation}/#/${clientCode}/conversation/${data.conversationId}`,
                launchType: 'modal',
                size: {
                    width: 620,
                    height: 600,
                },
            },
        };
    }

    private cacheImportKeysForNamedQuery(namedQueryId: string, schema: ConversableType): void {
        this.crosstalkSchemaFields.set(
            namedQueryId,
            schema.fields.map((field) => {
                return {
                    name: `${field}_crosstalk_key`,
                    displayName: field,
                };
            }));
    }

    private addCrosstalkAttachmentsToData(
        data: (WidgetData & { links: { [key: string]: LinkConfiguration } }),
        clientCode: string,
        highlight: ConversationHighlight,
        isUserEntitledToUploadAttachments: boolean,
    ): void {
        data[CrosstalkFields.Attachments] = highlight.lastAttachments?.length ? 'Attachments' : 'No Attachments';

        data.links = {
            ...data.links,
            [CrosstalkFields.Attachments]: {
                label: 'Add Comment',
                uri: `${this.xtlkManagementLocation}/#/${clientCode}/conversation/${highlight.id}`,
                launchType: 'modal',
                size: {
                    width: 620,
                    height: 600,
                },
                conversationId: highlight.id,
                clientCode,
                isUserEntitledToUploadAttachments,
                lastAttachments: highlight.lastAttachments,
            },
        };
    }
}
